import { useTranslation } from 'react-i18next';
import type { SettledFilterKey, UseMyBetsResult } from '@/betslip/useMyBets';
import { useCashout } from '@/betslip/useCashout';
import { useMyBetsLiveStatus } from '@/betslip/useMyBetsLiveStatus';
import { MIN_CASHOUT_OFFER } from '@/domain/cashout';
import type { MyBetsStatusTab } from '@/domain/tickets';
import { cn } from '@/lib/cn';
import { EmptyState } from '../common/empty-state';
import { Icon } from '../common/icon';
import { Spinner } from '../common/spinner';
import { Toast } from '../common/toast';
import { MyBetTicketCard } from './MyBetTicketCard';

export interface MyBetsPanelProps {
  myBets: UseMyBetsResult;
  currency?: string;
  onReuse?: (ticketId: string) => void;
  reusePendingTicketId?: string | null;
  reuseError?: string | null;
  onDismissReuseError?: () => void;
  className?: string;
}

const STATUS_TABS: {
  value: MyBetsStatusTab;
  labelKey: string;
  defaultLabel: string;
}[] = [
  { value: 'open', labelKey: 'Open', defaultLabel: 'Open' },
  { value: 'live', labelKey: 'Live', defaultLabel: 'Live' },
  { value: 'settled', labelKey: 'Settled', defaultLabel: 'Settled' },
  { value: 'saved', labelKey: 'Saved', defaultLabel: 'Saved' },
];

const SETTLED_FILTER_TOGGLES: {
  key: SettledFilterKey;
  labelKey: string;
  defaultLabel: string;
}[] = [
  { key: 'won', labelKey: 'Won', defaultLabel: 'Won' },
  { key: 'lost', labelKey: 'Lost', defaultLabel: 'Lost' },
  { key: 'sold', labelKey: 'Sold', defaultLabel: 'Sold' },
  { key: 'cancelled', labelKey: 'Cancelled', defaultLabel: 'Cancelled' },
  { key: 'bonus', labelKey: 'Bonus', defaultLabel: 'Bonus' },
];

function CountBadge({
  label,
  active,
}: {
  label: string;
  active: boolean;
}) {
  return (
    <span
      className={cn(
        'absolute -top-1 -right-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full px-1',
        'text-[9px] leading-none font-bold text-text-on-action',
        active ? 'bg-white/25' : 'bg-action-primary',
      )}
    >
      {label}
    </span>
  );
}

function statusCount(
  tab: MyBetsStatusTab,
  myBets: UseMyBetsResult,
): number | null {
  switch (tab) {
    case 'open':
      return myBets.openCount;
    case 'live':
      return myBets.liveCount;
    case 'settled':
      return myBets.settledCount;
    case 'saved':
      return myBets.savedCount;
  }
}

/**
 * My bets body: status pills, date filter, infinite ticket list with cashout.
 */
export function MyBetsPanel({
  myBets,
  currency = '',
  onReuse,
  reusePendingTicketId = null,
  reuseError = null,
  onDismissReuseError,
  className,
}: MyBetsPanelProps) {
  const { t } = useTranslation();
  const {
    statusTab,
    setStatusTab,
    dateDays,
    setDateDays,
    datePresets,
    showDateFilter,
    settledFilters,
    setSettledFilter,
    tickets,
    isLoading,
    isFetchingNextPage,
    listError,
    sentinelRef,
  } = myBets;

  const cashoutActive = statusTab === 'open' || statusTab === 'live';
  const liveUiActive = statusTab !== 'saved';
  const cashout = useCashout(tickets, cashoutActive);
  const liveStatus = useMyBetsLiveStatus(tickets, liveUiActive, cashoutActive);

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col gap-2', className)}>
      <div
        role="tablist"
        aria-label={t('MyBetsStatus', { defaultValue: 'Bet status' })}
        className="flex shrink-0 items-center gap-2 pt-1 pr-0.5"
      >
        {STATUS_TABS.map((tab) => {
          const active = statusTab === tab.value;
          const count = statusCount(tab.value, myBets);
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setStatusTab(tab.value)}
              className={cn(
                'relative flex min-w-0 flex-1 items-center justify-center overflow-visible rounded-md px-2 py-2',
                active
                  ? 'bg-action-primary text-text-on-action'
                  : 'bg-bg-elevated text-text-secondary',
              )}
            >
              <span
                className={cn(
                  'truncate text-xs',
                  active ? 'font-bold' : 'font-normal',
                )}
              >
                {t(tab.labelKey, { defaultValue: tab.defaultLabel })}
              </span>
              {count != null && count > 0 ? (
                <CountBadge label={String(count)} active={active} />
              ) : null}
            </button>
          );
        })}
      </div>

      {showDateFilter ? (
        <label className="relative block w-full shrink-0">
          <span className="sr-only">
            {t('DateFilter', { defaultValue: 'Date filter' })}
          </span>
          <select
            value={dateDays}
            onChange={(event) =>
              setDateDays(Number(event.target.value) as typeof dateDays)
            }
            className={cn(
              'h-9 w-full appearance-none rounded-md border border-border-subtle bg-bg-elevated',
              'px-3 py-2 pr-8 text-xs text-text-primary outline-none',
              'focus-visible:ring-1 focus-visible:ring-border-focus',
            )}
          >
            {datePresets.map((preset) => (
              <option key={preset.days} value={preset.days}>
                {t(preset.labelKey, { defaultValue: preset.defaultLabel })}
              </option>
            ))}
          </select>
          <Icon
            name="chevron-down"
            size={12}
            className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-icon-muted"
          />
        </label>
      ) : null}

      {statusTab === 'settled' ? (
        <div
          role="group"
          aria-label={t('SettledFilters', { defaultValue: 'Settled filters' })}
          className="flex shrink-0 flex-wrap gap-1.5"
        >
          {SETTLED_FILTER_TOGGLES.map((filter) => {
            const active = settledFilters[filter.key];
            return (
              <button
                key={filter.key}
                type="button"
                aria-pressed={active}
                onClick={() => setSettledFilter(filter.key, !active)}
                className={cn(
                  'rounded-md px-2.5 py-1.5 text-xs',
                  active
                    ? 'bg-action-primary font-bold text-text-on-action'
                    : 'bg-bg-elevated font-normal text-text-secondary',
                )}
              >
                {t(filter.labelKey, { defaultValue: filter.defaultLabel })}
              </button>
            );
          })}
        </div>
      ) : null}

      {reuseError ? (
        <Toast tone="danger" onDismiss={onDismissReuseError}>
          {reuseError}
        </Toast>
      ) : null}

      {cashout.errorMessage ? (
        <Toast tone="danger" onDismiss={cashout.clearMessages}>
          {cashout.errorMessage}
        </Toast>
      ) : null}

      {cashout.infoMessage ? (
        <Toast tone="warning" onDismiss={cashout.clearMessages}>
          {cashout.infoMessage}
        </Toast>
      ) : null}

      {listError ? (
        <Toast tone="danger">
          {listError.message || t('Error', { defaultValue: 'Error' })}
        </Toast>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center py-10">
            <Spinner label={t('Loading', { defaultValue: 'Loading' })} />
          </div>
        ) : tickets.length === 0 ? (
          <EmptyState
            icon="mytickets"
            title={t('NoBets', { defaultValue: 'You have no bets' })}
            description={t('NoBetsDescription', {
              defaultValue: 'Open tickets will appear here.',
            })}
            className="border-0 bg-transparent py-8"
          />
        ) : (
          <>
            {tickets.map((ticket) => {
              const eligible =
                cashout.cashoutEnabled && cashout.isTicketEligible(ticket);
              return (
                <MyBetTicketCard
                  key={ticket.Id}
                  ticket={ticket}
                  currency={currency}
                  onReuse={onReuse}
                  reusePending={reusePendingTicketId === ticket.Id}
                  liveStatus={liveUiActive ? liveStatus : null}
                  cashout={
                    eligible
                      ? {
                          offer: cashout.getOffer(ticket.Id),
                          updating:
                            cashout.isUpdating(ticket.Id) ||
                            (cashout.isListFetching &&
                              cashout.getOffer(ticket.Id) < MIN_CASHOUT_OFFER),
                          allowPartial: cashout.allowPartial,
                          history: cashout.getHistory(ticket.Id),
                          executing:
                            cashout.isExecuting &&
                            cashout.executingTicketId === ticket.Id,
                          onCashout: ({ cashoutValue, partialPercent }) => {
                            void cashout.executeCashout({
                              ticketId: ticket.Id,
                              cashoutValue,
                              partialPercent,
                            });
                          },
                          onAcknowledgeUpdating: () =>
                            cashout.acknowledgeUpdating(ticket.Id),
                        }
                      : null
                  }
                />
              );
            })}
            <div ref={sentinelRef} className="h-1 w-full shrink-0" aria-hidden />
            {isFetchingNextPage ? (
              <div className="flex justify-center py-2">
                <Spinner size="sm" label={null} />
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
